import React, { useEffect, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { PayrollService } from "services/payroll";

const STATUS_COLORS = { ACTIVE: "success", TERMINATED: "default", ON_LEAVE: "warning" };

function EmployeeForm({ initial = {}, onChange }) {
  const [form, setForm] = useState({
    rut: initial.rut || "",
    firstName: initial.firstName || "",
    lastName: initial.lastName || "",
    secondLastName: initial.secondLastName || "",
    email: initial.email || "",
    phone: initial.phone || "",
    position: initial.position || "",
    hireDate: initial.hireDate ? initial.hireDate.slice(0, 10) : new Date().toISOString().slice(0, 10),
    status: initial.status || "ACTIVE",
    address: initial.address || "",
  });
  useEffect(() => { onChange(form); }, [form]);
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, pt: 1 }}>
      <TextField label="RUT (con guión)" value={form.rut} onChange={(e) => setForm({ ...form, rut: e.target.value })} size="small" />
      <TextField label="Cargo" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} size="small" />
      <TextField label="Nombres" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} size="small" />
      <TextField label="Primer apellido" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} size="small" />
      <TextField label="Segundo apellido" value={form.secondLastName} onChange={(e) => setForm({ ...form, secondLastName: e.target.value })} size="small" />
      <TextField label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} size="small" />
      <TextField label="Teléfono" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} size="small" />
      <TextField label="Fecha de ingreso" type="date" InputLabelProps={{ shrink: true }} value={form.hireDate} onChange={(e) => setForm({ ...form, hireDate: e.target.value })} size="small" />
      <Select size="small" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
        <MenuItem value="ACTIVE">Activo</MenuItem>
        <MenuItem value="ON_LEAVE">Con licencia</MenuItem>
        <MenuItem value="TERMINATED">Desvinculado</MenuItem>
      </Select>
      <TextField label="Dirección" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} size="small" sx={{ gridColumn: "1 / 3" }} />
    </Box>
  );
}

export function EmployeesPanel({ memberId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await PayrollService.listEmployees(memberId);
      setItems(res.items || []);
    } catch (e) {
      setFeedback({ kind: "error", msg: e?.response?.data?.error || e.message });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [memberId]);

  const handleSubmit = async () => {
    try {
      if (editing) {
        await PayrollService.updateEmployee(memberId, editing._id, form);
        setFeedback({ kind: "ok", msg: "Empleado actualizado" });
      } else {
        await PayrollService.createEmployee(memberId, form);
        setFeedback({ kind: "ok", msg: "Empleado creado" });
      }
      setModalOpen(false);
      setEditing(null);
      await load();
    } catch (e) {
      setFeedback({ kind: "error", msg: e?.response?.data?.error || e.message });
    }
  };

  const handleEdit = (emp) => { setEditing(emp); setModalOpen(true); };
  const handleRemove = async (id) => {
    if (!window.confirm("¿Eliminar empleado?")) return;
    try { await PayrollService.deleteEmployee(memberId, id); await load(); }
    catch (e) { setFeedback({ kind: "error", msg: e?.response?.data?.error || e.message }); }
  };

  return (
    <Box>
      {feedback && <Alert severity={feedback.kind === "ok" ? "success" : "error"} onClose={() => setFeedback(null)} sx={{ mb: 2 }}>{feedback.msg}</Alert>}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditing(null); setForm({}); setModalOpen(true); }}>
          Nuevo empleado
        </Button>
      </Box>
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>RUT</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Cargo</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Ingreso</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}><CircularProgress size={20} /></TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: "text.secondary" }}>Sin empleados</TableCell></TableRow>
              ) : items.map((e) => (
                <TableRow key={e._id} hover>
                  <TableCell><code>{e.rut}</code></TableCell>
                  <TableCell>{e.firstName} {e.lastName} {e.secondLastName}</TableCell>
                  <TableCell>{e.position || "—"}</TableCell>
                  <TableCell>{e.email || "—"}</TableCell>
                  <TableCell>{e.hireDate ? new Date(e.hireDate).toLocaleDateString("es-CL") : "—"}</TableCell>
                  <TableCell><Chip size="small" label={e.status} color={STATUS_COLORS[e.status] || "default"} /></TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(e)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleRemove(e._id)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={modalOpen} onClose={() => { setModalOpen(false); setEditing(null); }} fullWidth maxWidth="md">
        <DialogTitle>{editing ? "Editar empleado" : "Nuevo empleado"}</DialogTitle>
        <DialogContent>
          <EmployeeForm initial={editing || {}} onChange={setForm} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setModalOpen(false); setEditing(null); }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubmit}>{editing ? "Guardar" : "Crear"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
